import React from 'react';
import styled from 'styled-components';
import { FiAlertTriangle, FiArrowRight } from 'react-icons/fi';
import { colors, typography } from '../../theme/colors';

// Compact horizontal strip of "stuck" chains — chains whose newest doc is
// older than `staleAfterDays` and which haven't reached the terminal lane.
// Click → focuses the canvas on that chain.
const CycleStuckList = ({ stuck, onSelectChain, staleAfterDays = 30 }) => {
  if (!stuck || stuck.length === 0) {
    return (
      <Empty>
        <FiAlertTriangle size={12} />
        <span>No stuck chains. Everything's moving along.</span>
      </Empty>
    );
  }

  return (
    <Bar>
      <BarLabel>
        <FiAlertTriangle size={12} />
        <strong>{stuck.length}</strong> stuck chain{stuck.length === 1 ? '' : 's'} · open &gt; {staleAfterDays}d
      </BarLabel>
      <Items>
        {stuck.slice(0, 12).map((s, i) => (
          <Item
            key={`${s.chain.rootId}-${i}`}
            onClick={() => onSelectChain && onSelectChain(s.chain)}
            title={`${s.rootDocNumber} → stuck at ${s.bottleneckNode.tranTypeCode} ${s.bottleneckNode.docNumber}`}
          >
            <Days>{s.ageDays}d</Days>
            <Doc>{s.rootTranType} {s.rootDocNumber}</Doc>
            <FiArrowRight size={10} />
            <Doc>{s.bottleneckNode.tranTypeCode}</Doc>
          </Item>
        ))}
        {stuck.length > 12 && <More>+{stuck.length - 12} more…</More>}
      </Items>
    </Bar>
  );
};

export default CycleStuckList;

const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  background: ${colors.error.main}08;
  border: 1px solid ${colors.error.main}40;
  border-radius: 6px;
  overflow-x: auto;
`;
const BarLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  font-weight: 600;
  color: ${colors.error.main};
  white-space: nowrap;
  strong { font-weight: 700; }
`;
const Items = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  flex-wrap: nowrap;
`;
const Item = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  background: white;
  border: 1px solid ${colors.error.main}30;
  border-radius: 14px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  color: ${colors.text.primary};
  font-family: ${typography.fontFamily.mono};
  &:hover {
    border-color: ${colors.error.main};
    background: ${colors.error.main}10;
  }
`;
const Days = styled.span`
  font-weight: 700;
  color: ${colors.error.main};
`;
const Doc = styled.span`
  color: ${colors.text.secondary};
  font-weight: 500;
`;
const More = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${colors.text.secondary};
  margin-left: 4px;
`;
const Empty = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 10px 14px;
  background: ${colors.success.main}10;
  border: 1px solid ${colors.success.main}40;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  color: ${colors.success.main};
`;
